import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { MotionPresetSpec } from '@bannermatic/core';
import { toastError } from './errors';
import { api, ApiError, uploadFile } from './api';
import { useSession } from './session';
import type {
  ApiKeyItem,
  Asset,
  AssetFolder,
  Automation,
  BatchItem,
  BillingInfo,
  FontsResponse,
  Integration,
  Invitation,
  LinkedSetItem,
  Member,
  RenderItem,
  TemplateDetail,
  TemplateSummary,
  WebhookDelivery,
  WorkspaceInfo,
} from './types';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: (err) => toastError(err),
    },
  },
});

/** Query keys are scoped by workspace so switching workspaces never shows stale data. */
export function useWs() {
  return useSession((s) => s.workspaceId);
}

export function useWorkspaceInfo() {
  const ws = useWs();
  return useQuery({
    queryKey: ['workspace', ws],
    queryFn: () => api<WorkspaceInfo>('/api/workspace'),
    enabled: !!ws,
  });
}

export function useTemplates(search = '') {
  const ws = useWs();
  return useQuery({
    queryKey: ['templates', ws, search],
    queryFn: () =>
      api<{ items: TemplateSummary[]; total: number }>(
        `/api/templates?limit=500${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      ),
    enabled: !!ws,
    // Previews render in the background (linked sizes too after a master edit): pick them up
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.thumbnailPending) ? 2000 : false),
  });
}

/** Fresh template detail (also used to open another size without showing a loader). */
export const templateQuery = (ws: string | null | undefined, id: string) => ({
  queryKey: ['template', ws, id],
  queryFn: () => api<TemplateDetail>(`/api/templates/${id}`),
});

export function useTemplate(id: string | undefined) {
  const ws = useWs();
  return useQuery({ ...templateQuery(ws, id!), enabled: !!ws && !!id, staleTime: 0 });
}

/** Every template of a linked-sizes set with documents, keyed by its master. */
export function useLinkedSet(masterId: string | null | undefined, enabled = true) {
  const ws = useWs();
  return useQuery({
    queryKey: ['linked-set', ws, masterId],
    queryFn: () => api<{ items: LinkedSetItem[] }>(`/api/templates/${masterId}/linked-set`),
    enabled: !!ws && !!masterId && enabled,
    staleTime: 0,
  });
}

export function useTemplatesDetails(ids: string[]) {
  const ws = useWs();
  return useQuery({
    queryKey: ['templates-detail', ws, [...ids].sort().join(',')],
    // One missing template (deleted, or from another workspace) must not hide the others
    queryFn: async () => {
      const results = await Promise.allSettled(
        ids.map((id) => api<TemplateDetail>(`/api/templates/${id}`)),
      );
      const items: TemplateDetail[] = [];
      const missing: string[] = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') items.push(r.value);
        else if (r.reason instanceof ApiError && r.reason.status === 404) missing.push(ids[i]);
        else throw r.reason;
      });
      return { items, missing };
    },
    enabled: !!ws && ids.length > 0,
  });
}

/** Media library files: `folder` is a folder id or "root"; empty means every file. */
export function useAssets(filter: { folder?: string | null; search?: string } = {}) {
  const ws = useWs();
  const qs = new URLSearchParams({ limit: '500' });
  if (filter.folder) qs.set('folder', filter.folder);
  if (filter.search?.trim()) qs.set('search', filter.search.trim());
  return useQuery({
    queryKey: ['assets', ws, filter.folder ?? '', filter.search?.trim() ?? ''],
    queryFn: () => api<{ items: Asset[] }>(`/api/assets?${qs}`),
    enabled: !!ws,
    placeholderData: (prev) => prev,
  });
}

export function useAssetFolders() {
  const ws = useWs();
  return useQuery({
    queryKey: ['assets', ws, 'folders'],
    queryFn: () => api<{ folders: AssetFolder[]; rootCount: number }>('/api/assets/folders'),
    enabled: !!ws,
  });
}

export function useUploadAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: File | { file: File; folderId?: string | null }) =>
      input instanceof File
        ? uploadFile<Asset>('/api/assets', input)
        : uploadFile<Asset>(
            '/api/assets',
            input.file,
            input.folderId ? { folderId: input.folderId } : {},
          ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assets'] }),
  });
}

export function useFonts() {
  const ws = useWs();
  return useQuery({
    queryKey: ['fonts', ws],
    queryFn: () => api<FontsResponse>('/api/fonts'),
    enabled: !!ws,
    staleTime: 60_000,
  });
}

export function useRenders(filters: Record<string, string | undefined>) {
  const ws = useWs();
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v) as Array<[string, string]>,
  ).toString();
  return useInfiniteQuery({
    queryKey: ['renders', ws, qs],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<{ items: RenderItem[]; nextCursor: string | null }>(
        `/api/renders?limit=60&${qs}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!ws,
  });
}

/**
 * Deletes generated images: a DELETE on `path`, or a POST with `body` (bulk), then refreshes
 * every list that shows them.
 */
export async function deleteGenerated(
  path: string,
  body?: unknown,
): Promise<{ deleted: number; skipped?: number }> {
  let res: { deleted: number; skipped?: number };
  try {
    res = await api(path, body === undefined ? { method: 'DELETE' } : { body });
  } catch (err) {
    toastError(err); // e.g. a run still in progress
    throw err;
  }
  await queryClient.invalidateQueries({
    predicate: (q) =>
      ['renders', 'batches', 'batch', 'batch-issues'].includes(String(q.queryKey[0])),
  });
  return res;
}

export function useBatches(automationId?: string, opts: { refetch?: boolean } = {}) {
  const ws = useWs();
  return useQuery({
    queryKey: ['batches', ws, automationId ?? 'all'],
    queryFn: () =>
      api<{ items: BatchItem[] }>(
        `/api/batches?limit=50${automationId ? `&automationId=${automationId}` : ''}`,
      ),
    enabled: !!ws,
    refetchInterval: (q) =>
      opts.refetch &&
      q.state.data?.items.some((b) => b.status === 'pending' || b.status === 'processing')
        ? 2000
        : false,
  });
}

export function useBatch(id: string | undefined) {
  const ws = useWs();
  return useQuery({
    queryKey: ['batch', ws, id],
    queryFn: () => api<BatchItem>(`/api/batches/${id}`),
    enabled: !!ws && !!id,
    refetchInterval: (q) =>
      q.state.data && !q.state.data.finishedAt && q.state.data.status !== 'failed' ? 1500 : false,
  });
}

export function useAutomations() {
  const ws = useWs();
  return useQuery({
    queryKey: ['automations', ws],
    queryFn: () => api<{ items: Automation[] }>('/api/automations'),
    enabled: !!ws,
  });
}

export function useAutomation(id: string | undefined) {
  const ws = useWs();
  return useQuery({
    queryKey: ['automation', ws, id],
    queryFn: () => api<Automation>(`/api/automations/${id}`),
    enabled: !!ws && !!id && id !== 'new',
  });
}

export interface MotionPresetItem {
  id: string;
  category: MotionPresetSpec['category'];
  name: string;
  description: string;
  source: 'editor' | 'api' | 'mcp';
  spec: MotionPresetSpec;
  createdAt: string;
  updatedAt: string;
}

/** Custom motion presets of the workspace (made in the API or by an AI agent). */
export function useMotionPresets() {
  const ws = useWs();
  return useQuery({
    queryKey: ['motion-presets', ws],
    queryFn: () => api<{ items: MotionPresetItem[] }>('/api/motion-presets'),
    enabled: !!ws,
    staleTime: 30_000,
  });
}

export function useApiKeys() {
  const ws = useWs();
  return useQuery({
    queryKey: ['api-keys', ws],
    queryFn: () => api<{ items: ApiKeyItem[] }>('/api/api-keys'),
    enabled: !!ws,
  });
}

export function useIntegrations() {
  const ws = useWs();
  return useQuery({
    queryKey: ['integrations', ws],
    queryFn: () =>
      api<{ items: Integration[]; googleEnabled: boolean; googlePicker?: boolean }>(
        '/api/integrations',
      ),
    enabled: !!ws,
  });
}

export function useWebhookDeliveries() {
  const ws = useWs();
  return useQuery({
    queryKey: ['webhook-deliveries', ws],
    queryFn: () => api<{ items: WebhookDelivery[] }>('/api/integrations/webhooks/deliveries'),
    enabled: !!ws,
    refetchInterval: 5000,
  });
}

export function useMembers() {
  const ws = useWs();
  return useQuery({
    queryKey: ['members', ws],
    queryFn: () => api<{ members: Member[]; invitations: Invitation[] }>('/api/workspace/members'),
    enabled: !!ws,
  });
}

export function useBilling() {
  const ws = useWs();
  return useQuery({
    queryKey: ['billing', ws],
    queryFn: () => api<BillingInfo>('/api/billing'),
    enabled: !!ws,
  });
}

export async function refreshSession() {
  try {
    const me = await api<{ user: any; workspaces: any[] }>('/api/auth/me', { workspace: false });
    useSession.getState().setSession(me.user, me.workspaces);
    return me.user ? me : null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      useSession.getState().setSession(null, []);
      return null;
    }
    throw err;
  }
}

export interface AiStatus {
  enabled: boolean;
  provider: 'gemini' | 'mock' | 'off';
  used: number;
  limit: number;
  remaining: number;
}

export function useAiStatus(enabled = true) {
  const ws = useSession((s) => s.workspaceId);
  return useQuery({
    queryKey: ['ai-status', ws],
    queryFn: () => api<AiStatus>('/api/ai/status'),
    enabled: enabled && !!ws,
  });
}
